// All sounds here are synthesized in-browser with the Web Audio API — no external
// audio files, so there's zero licensing risk and nothing to download.

const MUTE_STORAGE_KEY = "world_audio_muted";

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let noiseBuffer: AudioBuffer | null = null;

const readStoredMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

let muted = readStoredMuted();

const getCtx = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  const AudioCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtor) return null;
  if (!ctx) {
    ctx = new AudioCtor();
    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : 0.9;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

export const initAudio = () => {
  getCtx();
};

export const isAudioMuted = () => muted;

export const setAudioMuted = (value: boolean) => {
  muted = value;
  const c = getCtx();
  if (c && masterGain) {
    masterGain.gain.setTargetAtTime(muted ? 0 : 0.9, c.currentTime, 0.06);
  }
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, String(value));
  } catch {
    /* ignore persistence failures */
  }
};

const getNoiseBuffer = (c: AudioContext): AudioBuffer => {
  if (!noiseBuffer) {
    const length = c.sampleRate * 2;
    noiseBuffer = c.createBuffer(1, length, c.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  }
  return noiseBuffer;
};

// ---- One-shot effects ----

export const playFootstep = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const src = c.createBufferSource();
  src.buffer = getNoiseBuffer(c);
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 260 + Math.random() * 160;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, c.currentTime);
  gain.gain.linearRampToValueAtTime(0.28, c.currentTime + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.1);
  src.connect(filter).connect(gain).connect(masterGain);
  src.start();
  src.stop(c.currentTime + 0.12);
};

export const playJump = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(220, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(540, c.currentTime + 0.15);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.22, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.2);
  osc.connect(gain).connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 0.22);
};

export const playBrakeSound = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const src = c.createBufferSource();
  src.buffer = getNoiseBuffer(c);
  const filter = c.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 900;
  filter.Q.value = 0.8;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.1, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.22);
  src.connect(filter).connect(gain).connect(masterGain);
  src.start();
  src.stop(c.currentTime + 0.22);
};

/** A short, soft throttle blip — plays once when the car starts moving from a stop. */
export const playAccelerateSound = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const osc = c.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(70, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(130, c.currentTime + 0.25);
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 500;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, c.currentTime);
  gain.gain.linearRampToValueAtTime(0.09, c.currentTime + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.35);
  osc.connect(filter).connect(gain).connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 0.35);
};

export const playChirp = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const osc = c.createOscillator();
  osc.type = "sine";
  const base = 1700 + Math.random() * 900;
  osc.frequency.setValueAtTime(base, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(base * 1.35, c.currentTime + 0.07);
  osc.frequency.exponentialRampToValueAtTime(base * 0.75, c.currentTime + 0.16);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.07, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.2);
  osc.connect(gain).connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 0.22);
};

export const playChatterBlip = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const osc = c.createOscillator();
  osc.type = "triangle";
  const base = 130 + Math.random() * 90;
  osc.frequency.setValueAtTime(base, c.currentTime);
  osc.frequency.linearRampToValueAtTime(base * (0.75 + Math.random() * 0.6), c.currentTime + 0.11);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.05, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.13);
  osc.connect(gain).connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 0.15);
};

export const playRabbitSound = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const osc = c.createOscillator();
  osc.type = "sine";
  const base = 2400 + Math.random() * 500;
  osc.frequency.setValueAtTime(base, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(base * 1.5, c.currentTime + 0.04);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.045, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.09);
  osc.connect(gain).connect(masterGain);
  osc.start();
  osc.stop(c.currentTime + 0.1);
};

/** A warbling "whinny" — carrier tone with vibrato (pitch LFO) and a rising/falling contour. */
export const playHorseSound = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const now = c.currentTime;
  const duration = 0.85;
  const base = 260 + Math.random() * 40;

  const carrier = c.createOscillator();
  carrier.type = "sawtooth";
  carrier.frequency.setValueAtTime(base, now);
  carrier.frequency.linearRampToValueAtTime(base * 1.5, now + 0.16);
  carrier.frequency.linearRampToValueAtTime(base * 1.15, now + 0.38);
  carrier.frequency.linearRampToValueAtTime(base * 1.3, now + 0.55);
  carrier.frequency.exponentialRampToValueAtTime(base * 0.65, now + duration);

  // Vibrato: an LFO modulating the carrier's own pitch for a warbling texture
  const lfo = c.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 17;
  const lfoGain = c.createGain();
  lfoGain.gain.value = 16;
  lfo.connect(lfoGain).connect(carrier.frequency);

  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1300;

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.linearRampToValueAtTime(0.085, now + 0.07);
  gain.gain.setValueAtTime(0.075, now + duration * 0.55);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  carrier.connect(filter).connect(gain).connect(masterGain);
  carrier.start(now);
  lfo.start(now);
  carrier.stop(now + duration);
  lfo.stop(now + duration);
};

/** A quick double-snort, closer to how gazelles/antelope actually signal — percussive, not tonal. */
export const playGazelleSound = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const now = c.currentTime;
  const snort = (offset: number) => {
    const src = c.createBufferSource();
    src.buffer = getNoiseBuffer(c);
    const filter = c.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 500 + Math.random() * 200;
    filter.Q.value = 2.2;
    const gain = c.createGain();
    gain.gain.setValueAtTime(0, now + offset);
    gain.gain.linearRampToValueAtTime(0.09, now + offset + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.09);
    src.connect(filter).connect(gain).connect(masterGain!);
    src.start(now + offset);
    src.stop(now + offset + 0.1);
  };
  snort(0);
  snort(0.12 + Math.random() * 0.03);
};

// ---- Looping ambient sounds ----

interface AmbientHandle {
  gain: GainNode | null;
}

const waterHandle: AmbientHandle = { gain: null };
let engineOsc: OscillatorNode | null = null;
let engineGain: GainNode | null = null;

export const startWaterLoop = () => {
  const c = getCtx();
  if (!c || !masterGain || waterHandle.gain) return;
  const src = c.createBufferSource();
  src.buffer = getNoiseBuffer(c);
  src.loop = true;
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1800;
  const gain = c.createGain();
  gain.gain.value = 0;
  src.connect(filter).connect(gain).connect(masterGain);
  src.start();
  waterHandle.gain = gain;
};

/** 0 (silent) .. 1 (max) — call every frame with proximity-based volume. */
export const setWaterVolume = (v: number) => {
  const c = getCtx();
  if (!c || !waterHandle.gain) return;
  waterHandle.gain.gain.setTargetAtTime(Math.max(0, Math.min(1, v)) * 0.35, c.currentTime, 0.2);
};

export const startEngine = () => {
  const c = getCtx();
  if (!c || !masterGain || engineOsc) return;
  engineOsc = c.createOscillator();
  engineOsc.type = "sawtooth";
  engineOsc.frequency.value = 60;
  engineGain = c.createGain();
  engineGain.gain.value = 0.05;
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 400;
  engineOsc.connect(filter).connect(engineGain).connect(masterGain);
  engineOsc.start();
};

export const stopEngine = () => {
  engineOsc?.stop();
  engineOsc = null;
  engineGain = null;
};

/** speedFrac: 0 (idle) .. 1 (max speed) — call every frame while driving. */
export const updateEngine = (speedFrac: number) => {
  const c = getCtx();
  if (!c || !engineOsc || !engineGain) return;
  const clamped = Math.max(0, Math.min(1, speedFrac));
  engineOsc.frequency.setTargetAtTime(60 + clamped * 120, c.currentTime, 0.1);
  engineGain.gain.setTargetAtTime(0.04 + clamped * 0.09, c.currentTime, 0.1);
};
